import { Children, cloneElement, isValidElement, useCallback, useEffect, useRef } from 'react';
import { TabProps } from '../Tab';
import { ClassNamed } from '../types';
import { ArrowLeftContainer, ArrowRightContainer, SlideAnimatorElement } from './style';

export interface TabsProps<T = string> extends ClassNamed, Pick<TabProps, 'underline' | 'inline'> {
    activeTabValue?: T;
    children: Array<React.ReactElement<TabProps>> | React.ReactElement<TabProps>;
    inline?: boolean;
    onTabClick?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
    showDivider?: boolean;
}

export const Tabs = ({
    activeTabValue,
    inline,
    underline = true,
    children,
    onTabClick,
    className,
    showDivider = false,
    ...other
}: TabsProps) => {
    const ref = useRef<HTMLOListElement>(null);

    const scrollActiveTabIntoView = useCallback(() => {
        if (!activeTabValue && ref.current) {
            ref.current.querySelector('[data-tab-item-active="true"]')?.scrollIntoView({
                inline: 'center',
                behavior: 'smooth',
            });
        }
    }, [activeTabValue]);

    useEffect(() => {
        scrollActiveTabIntoView();
    }, [scrollActiveTabIntoView]);

    const handleTabClick = (event: React.MouseEvent<HTMLElement>, value: string) => {
        onTabClick?.(event, value);

        // Wait for next tick to allow DOM updates
        window.setTimeout(() => {
            scrollActiveTabIntoView();
        }, 0);
    };

    return (
        <SlideAnimatorElement
            selectedChildId={activeTabValue}
            alignToNearestOnSwipeEnd
            className={className}
            role="tablist"
            showArrows
            arrowLeftContainer={ArrowLeftContainer}
            arrowRightContainer={ArrowRightContainer}
            showDivider={inline && showDivider}
            ref={ref}
        >
            {Children.map(children, (child) => {
                if (!isValidElement<TabProps>(child)) {
                    return child;
                }

                const itemValue = child.props.value || '';

                return cloneElement(child, {
                    ...other,
                    ...child.props,
                    key: child.props.id,
                    inline,
                    active: itemValue === activeTabValue,
                    underline,
                    onClick: handleTabClick,
                    showDivider,
                });
            })}
        </SlideAnimatorElement>
    );
};
